import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { MOCK_ORDERS, MockOrder } from '../mock-data/mock-orders.data';

/**
 * Responsive Example Component
 * Demonstrates responsive features and adaptive design for mobile/tablet
 */
@Component({
  selector: 'app-responsive-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent, MatButtonModule, MatIconModule],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Diseño Responsive</h2>
        <p class="text-gray-600 mb-2">
          El Smart Grid se adapta automáticamente a diferentes tamaños de pantalla: desktop, tablet y móvil.
        </p>
        <div class="flex gap-2 flex-wrap mb-4">
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            🖥️ Desktop Optimized
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            📱 Mobile Ready
          </span>
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            🔽 Auto-hide Columns
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            ⚡ Touch Optimized
          </span>
        </div>
      </div>

      <!-- Viewport Size Indicator -->
      <div class="p-4 bg-blue-50 dark:bg-blue-900 rounded mb-4">
        <h3 class="font-semibold mb-2 flex items-center gap-2">
          <mat-icon class="text-blue-600">devices</mat-icon>
          Tamaño de Viewport Actual:
        </h3>
        <div class="text-sm">
          <p><strong>Ancho:</strong> {{ viewportWidth }}px</p>
          <p><strong>Breakpoint:</strong>
            <span class="font-bold" [class]="getBreakpointClass()">{{ currentBreakpoint }}</span>
          </p>
          <p class="text-gray-600 dark:text-gray-400 mt-2">
            💡 Redimensiona la ventana del navegador para ver cómo el grid se adapta automáticamente
          </p>
        </div>
      </div>

      <!-- Responsive Features Info -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div class="p-4 bg-green-50 dark:bg-green-900 rounded">
          <h4 class="font-semibold flex items-center gap-2 mb-2">
            <mat-icon class="text-green-600">desktop_windows</mat-icon>
            Desktop (>1024px)
          </h4>
          <ul class="text-sm space-y-1">
            <li>✓ Todas las columnas visibles</li>
            <li>✓ Controles estándar</li>
            <li>✓ Densidad confortable</li>
            <li>✓ Hover effects</li>
          </ul>
        </div>

        <div class="p-4 bg-purple-50 dark:bg-purple-900 rounded">
          <h4 class="font-semibold flex items-center gap-2 mb-2">
            <mat-icon class="text-purple-600">tablet</mat-icon>
            Tablet (641-1024px)
          </h4>
          <ul class="text-sm space-y-1">
            <li>✓ Columnas prioritarias</li>
            <li>✓ Modo compacto</li>
            <li>✓ Touch targets más grandes</li>
            <li>✓ Scroll horizontal suave</li>
          </ul>
        </div>

        <div class="p-4 bg-orange-50 dark:bg-orange-900 rounded">
          <h4 class="font-semibold flex items-center gap-2 mb-2">
            <mat-icon class="text-orange-600">phone_android</mat-icon>
            Mobile (<640px)
          </h4>
          <ul class="text-sm space-y-1">
            <li>✓ Solo columnas críticas</li>
            <li>✓ Vista de cards opcional</li>
            <li>✓ Touch optimizado (44px)</li>
            <li>✓ Stack de columnas</li>
          </ul>
        </div>
      </div>

      <!-- Grid with Responsive Configuration -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          [data]="gridData"
          [columnDefs]="columnDefs"
          [config]="gridConfig">
        </app-smart-grid>
      </div>

      <!-- Configuration Example -->
      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">⚙️ Configuración Responsive:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{configExample}}</code></pre>
      </div>

      <!-- Features List -->
      <div class="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded">
        <h3 class="font-semibold mb-2">✨ Características Responsive:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li><strong>Breakpoints Configurables:</strong> Define tus propios puntos de quiebre (mobile/tablet/desktop)</li>
          <li><strong>Auto-hide Columns:</strong> Oculta automáticamente columnas menos importantes en pantallas pequeñas</li>
          <li><strong>Column Priority:</strong> Define prioridad de columnas para control granular</li>
          <li><strong>Compact Mode:</strong> Reduce padding y tamaños de fuente en tablets</li>
          <li><strong>Touch Targets:</strong> Aumenta el tamaño de botones/checkboxes a mínimo 44px</li>
          <li><strong>Card View Mode:</strong> Vista de tarjetas alternativa para móviles (próximamente)</li>
          <li><strong>Stack Columns:</strong> Apila múltiples campos verticalmente en móvil</li>
          <li><strong>Swipe Gestures:</strong> Gestos táctiles para acciones rápidas (próximamente)</li>
        </ul>
      </div>

      <!-- Best Practices -->
      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">📱 Mejores Prácticas para Mobile:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li>Define 3-4 columnas máximo para mobile</li>
          <li>Marca columnas críticas que siempre deben mostrarse</li>
          <li>Usa nombres cortos para headers en mobile</li>
          <li>Habilita scroll horizontal para datos extensos</li>
          <li>Considera card view para diseños complejos</li>
          <li>Prueba touch targets en dispositivos reales</li>
        </ul>
      </div>
    </div>
  `,
})
export class ResponsiveExampleComponent {
  gridData: MockOrder[] = MOCK_ORDERS.slice(0, 50);

  // Viewport tracking
  viewportWidth: number = window.innerWidth;
  currentBreakpoint: string = this.getBreakpointName();

  constructor() {
    // Listen to viewport changes
    window.addEventListener('resize', () => {
      this.viewportWidth = window.innerWidth;
      this.currentBreakpoint = this.getBreakpointName();
    });
  }

  private getBreakpointName(): string {
    if (this.viewportWidth <= 640) return 'Mobile';
    if (this.viewportWidth <= 1024) return 'Tablet';
    return 'Desktop';
  }

  getBreakpointClass(): string {
    if (this.viewportWidth <= 640) return 'text-orange-600';
    if (this.viewportWidth <= 1024) return 'text-purple-600';
    return 'text-green-600';
  }

  // Column definitions with responsive priorities
  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Orden #',
      width: 120,
      pinned: 'left',
      // Priority 1: Always visible (critical)
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 200,
      // Priority 2: Visible on mobile
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      valueFormatter: (value) => `${value.toFixed(2)}`,
      cellClass: 'text-right font-semibold',
      // Priority 3: Visible on mobile
    },
    {
      field: 'statusId',
      headerName: 'Estado',
      width: 120,
      valueFormatter: (value) => {
        const statusNames: Record<number, string> = {
          0: 'Anulado',
          1: 'Activo',
          6: 'En Progreso',
          7: 'Aprobado',
          8: 'Rechazado',
          18: 'Inactivo',
        };
        return statusNames[value] || 'Desconocido';
      },
      cellClass: (params) => {
        const statusClasses: Record<number, string> = {
          0: 'text-red-600 bg-red-50',
          1: 'text-green-600 bg-green-50',
          6: 'text-blue-600 bg-blue-50',
          7: 'text-purple-600 bg-purple-50',
          8: 'text-red-600 bg-red-50',
          18: 'text-gray-600 bg-gray-50',
        };
        return statusClasses[params.data?.statusId] || '';
      },
      // Priority 4: Hidden on mobile, visible on tablet+
    },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 150,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
      // Priority 5: Hidden on mobile, visible on tablet+
    },
    {
      field: 'soldTo.code',
      headerName: 'Código',
      width: 150,
      // Priority 6: Hidden on mobile/tablet, visible on desktop only
    },
    {
      field: 'subRoute.name',
      headerName: 'Ruta',
      width: 180,
      // Priority 7: Hidden on mobile/tablet, visible on desktop only
    },
    {
      field: 'warehouse.name',
      headerName: 'Almacén',
      width: 150,
      // Priority 8: Hidden on mobile/tablet, visible on desktop only
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    // Responsive configuration
    responsive: {
      enabled: true,
      breakpoints: {
        mobile: 640,   // 0-640px = Mobile
        tablet: 1024,  // 641-1024px = Tablet
      },
      mobileMode: 'compact', // or 'cards'
      autoHideColumns: true,

      // Specific columns to hide on different breakpoints
      hideColumnsOnMobile: [
        'statusId',
        'createdAt',
        'soldTo.code',
        'subRoute.name',
        'warehouse.name',
      ] as any[],

      hideColumnsOnTablet: [
        'soldTo.code',
        'subRoute.name',
        'warehouse.name',
      ] as any[],

      // Critical columns that should never be hidden
      criticalColumns: ['serial', 'soldTo.name', 'total'] as any[],

      // Compact mode on tablet
      compactModeOnTablet: true,

      // Touch optimization
      touchOptimized: true,
      minTouchTargetSize: 44,
    },

    // Other configurations
    pagination: {
      enabled: true,
      pageSize: 20,
      pageSizeOptions: [10, 20, 50],
    },
    sorting: {
      enabled: true,
      multiColumn: false,
    },
    search: {
      enabled: true,
      showSearchInput: true,
      placeholder: 'Buscar órdenes...',
      searchableFields: ['serial', 'soldTo.name', 'soldTo.code'],
    },
    selection: {
      enabled: true,
      mode: 'multiple',
      showCheckboxColumn: true,
    },
    styling: {
      maxHeight: '500px',
    },
  };

  configExample = `// Responsive Grid Configuration
gridConfig: GridConfig<MockOrder> = {
  responsive: {
    enabled: true,

    // Breakpoint definitions
    breakpoints: {
      mobile: 640,   // 0-640px = Mobile
      tablet: 1024,  // 641-1024px = Tablet
    },

    // Mobile view mode
    mobileMode: 'compact', // or 'cards', 'table'

    // Auto-hide columns based on priority
    autoHideColumns: true,

    // Specific columns to hide on mobile
    hideColumnsOnMobile: [
      'statusId',
      'createdAt',
      'soldTo.code',
      'subRoute.name',
    ],

    // Specific columns to hide on tablet
    hideColumnsOnTablet: [
      'soldTo.code',
      'subRoute.name',
    ],

    // Critical columns (never hidden)
    criticalColumns: [
      'serial',
      'soldTo.name',
      'total',
    ],

    // Compact mode for tablet
    compactModeOnTablet: true,

    // Touch optimization
    touchOptimized: true,
    minTouchTargetSize: 44, // pixels

    // Swipe gestures (future)
    enableSwipeGestures: false,
  },

  // ... other config
};`;
}
